// DS6.2
import React, { useContext, useMemo } from 'react';
import type { ItemClickBiEvent } from '@wix/da-bi/pkg/events';
import FolderLink from './_partials/FolderLink';
import FolderCardFooter, {
  Props as FooterProps,
} from './_partials/FolderCardFooter/FolderCardFooter';
import FolderCardContent from './_partials/FolderCardContent';
import FolderCardLayout from './_partials/FolderCardLayout';
import FolderRibbon from '../Primitives/FolderRibbon';
import { isPlainClick } from '../utils/clickHelpers';
import {
  getFooterHeight,
  getMobileFooterHeight,
  getFooterMargin,
  getPadding,
  shouldShowStats,
} from './helpers';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import classNames from 'classnames';

import s from './FolderCard.scss';

export type Props = Omit<FooterProps, 'size'> & {
  width: number;
  className?: string;
  statsClassName?: string;
  contentClickBiData?: ItemClickBiEvent;
  dataHook?: string;
  extras?: React.ReactNode;
  isSelected?: boolean;
  onClick?: () => void;
  indexInsideParent?: number;
  withLink?: boolean;
  showCountsWithoutCaptions?: boolean;
  hasSubfolders?: boolean;
  isParent?: boolean;
  disableMarginRight?: boolean;
};

export const FOLDER_CARD_ASPECT_RATIO = 1.18;

const preventDefault = e => !e.metaKey && !e.ctrlKey && e.preventDefault();
const stopPropagation = e => e.stopPropagation();

export const FolderCard: React.FC<React.PropsWithChildren<Props>> = ({
  extras,
  folder,
  hasSubfolders,
  isLocked,
  onClick,
  width,
  showCountsWithoutCaptions,
  oneLineDescription,
  withLink,
  withPrice,
  isSelected,
  isParent,
  disableMarginRight,
  ...props
}) => {
  const isMobile = useContext(MobileContext);
  const hasNewFolderFeature = useFlag('folders_layout');
  const height = width / FOLDER_CARD_ASPECT_RATIO;

  const previewWidth = hasNewFolderFeature ? width : width - getPadding();
  const previewHeight = useMemo(() => {
    if (hasNewFolderFeature) {
      if (isMobile) {
        return height - getPadding() / 2 - getMobileFooterHeight();
      } else {
        return (
          height -
          getPadding() / 2 -
          getFooterMargin() -
          getFooterHeight(height)
        );
      }
    } else {
      return (
        height - getPadding() - getFooterMargin() - getFooterHeight(height)
      );
    }
  }, [hasNewFolderFeature, height, isMobile]);

  const folderSize = folder.totalItemCount ?? folder.size ?? 0;

  return (
    <FolderCardLayout
      isSelected={isSelected}
      isParent={isParent}
      folder={folder}
      width={width}
      height={height}
      wrapCondition={withLink}
      disableMarginRight={disableMarginRight}
      wrap={(child: React.ReactNode) => (
        <FolderLink
          className={s['folder-link']}
          onClick={onClick ? preventDefault : undefined}
          folder={folder}
        >
          {child}
        </FolderLink>
      )}
      content={
        <FolderCardContent
          className={classNames(isMobile && s['mobile'])}
          width={previewWidth}
          height={previewHeight}
          folder={folder}
          hasSubfolders={hasSubfolders}
          totalDeviationCount={folderSize}
        />
      }
      ribbon={
        folder.premiumFolderData && (
          <FolderRibbon premiumFolderData={folder.premiumFolderData} />
        )
      }
      footer={
        <FolderCardFooter
          folder={folder}
          isLocked={isLocked}
          showCountsWithoutCaptions={showCountsWithoutCaptions}
          withPrice={withPrice}
          oneLineDescription={oneLineDescription}
          hideStats={!shouldShowStats(height)}
        >
          {extras && (
            <span className={s['extras']} onClick={stopPropagation}>
              {extras}
            </span>
          )}
        </FolderCardFooter>
      }
      {...props}
      onClick={e => {
        if (!onClick) {
          return;
        }
        // ctrl/cmd clicks open the link, but don't fire onClick handler
        if (withLink && !isPlainClick(e)) {
          return;
        }
        onClick();
      }}
    />
  );
};

export default FolderCard;
